import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useDebounceCallback,
} from "@devhop/ui";
import { Check, User } from "lucide-react";
import { useEffect, useState } from "react";
import {
  useCommentMessengerProfilesQuery,
  useLinkCommentMessengerProfile,
} from "@/modules/cf-feed/presentation/api/queries";
import type { FeedCommentRow } from "@/modules/cf-feed/presentation/api/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";

const PAGE_LIMIT = 10;

type LinkMessengerProfileDialogProps = {
  feedId: string;
  comment: FeedCommentRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function LinkMessengerProfileDialog({
  feedId,
  comment,
  open,
  onOpenChange,
}: LinkMessengerProfileDialogProps) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [profileId, setProfileId] = useState("");
  const debouncedSearch = useDebounceCallback((value: string) => {
    setSearch(value.trim());
    setOffset(0);
  }, 350);
  const profiles = useCommentMessengerProfilesQuery(
    feedId,
    comment?.id ?? "",
    { q: search, limit: PAGE_LIMIT, offset },
    open && Boolean(comment),
  );
  const linkProfile = useLinkCommentMessengerProfile(feedId);

  useEffect(() => {
    if (open) return;
    setSearchInput("");
    setSearch("");
    setOffset(0);
    setProfileId("");
  }, [open]);

  const total = profiles.data?.total ?? 0;
  const items = profiles.data?.items ?? [];
  const hasPrevious = offset > 0;
  const hasNext = offset + items.length < total;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>ເຊື່ອມລູກຄ້າ Messenger</DialogTitle>
          <DialogDescription>
            ເລືອກລູກຄ້າຂອງ Facebook Page ນີ້ໃຫ້ກົງກັບຄອມເມັນ
            {comment ? ` “${comment.commentText.slice(0, 60)}”` : ""}. ຫຼັງເຊື່ອມ
            ລະບົບຈະສົ່ງຂໍ້ຄວາມອໍເດີ້ພຽງຄັ້ງດຽວ.
          </DialogDescription>
        </DialogHeader>

        <Input
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            debouncedSearch(event.target.value);
          }}
          placeholder="ຄົ້ນຫາຊື່ລູກຄ້າ Messenger..."
          aria-label="ຄົ້ນຫາລູກຄ້າ Messenger"
        />

        <div className="space-y-2" aria-live="polite">
          {profiles.isLoading || profiles.isFetching ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              ກຳລັງຄົ້ນຫາ...
            </p>
          ) : profiles.isError ? (
            <div className="space-y-3 rounded-md border border-destructive/30 p-4 text-sm">
              <p>ໂຫຼດລາຍຊື່ Messenger ບໍ່ສຳເລັດ</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void profiles.refetch()}
              >
                ລອງໃໝ່
              </Button>
            </div>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              ບໍ່ພົບລູກຄ້າ Messenger ທີ່ກົງກັບຄຳຄົ້ນ
            </p>
          ) : (
            items.map((profile) => {
              const selected = profile.id === profileId;
              return (
                <button
                  key={profile.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setProfileId(profile.id)}
                  className={`flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors ${
                    selected
                      ? "border-primary bg-primary/5"
                      : "hover:bg-muted/60"
                  }`}
                >
                  <span className="size-10 shrink-0 overflow-hidden rounded-full border bg-muted">
                    <AppImage
                      src={profile.pictureUrl}
                      alt={profile.displayName}
                      className="size-full"
                      showLoading={false}
                      fallback={
                        <span className="flex size-full items-center justify-center text-muted-foreground">
                          <User className="size-5" />
                        </span>
                      }
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-sm">
                      {profile.displayName}
                    </span>
                    <span className="block text-muted-foreground text-xs">
                      {profile.lastMessengerInteractionAt
                        ? `ທັກ Messenger ຫຼ້າສຸດ ${formatDateTimeLocal(profile.lastMessengerInteractionAt)}`
                        : profile.lastSeenAt
                          ? `ພົບຫຼ້າສຸດ ${formatDateTimeLocal(profile.lastSeenAt)}`
                          : "ຍັງບໍ່ມີເວລາຕິດຕໍ່ຫຼ້າສຸດ"}
                    </span>
                  </span>
                  {selected ? <Check className="size-5 text-primary" /> : null}
                </button>
              );
            })
          )}
        </div>

        {total > PAGE_LIMIT ? (
          <div className="flex items-center justify-between text-muted-foreground text-xs">
            <span>
              {offset + 1}–{Math.min(offset + items.length, total)} / {total}
            </span>
            <span className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!hasPrevious || profiles.isFetching}
                onClick={() =>
                  setOffset((value) => Math.max(0, value - PAGE_LIMIT))
                }
              >
                ກ່ອນໜ້າ
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!hasNext || profiles.isFetching}
                onClick={() => setOffset((value) => value + PAGE_LIMIT)}
              >
                ຕໍ່ໄປ
              </Button>
            </span>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={linkProfile.isPending}
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            disabled={!comment || !profileId || linkProfile.isPending}
            onClick={async () => {
              if (!comment || !profileId) return;
              try {
                await linkProfile.mutateAsync({
                  commentLogId: comment.id,
                  profileId,
                });
                onOpenChange(false);
              } catch {
                // mutation แสดงข้อความผิดพลาดให้ผู้ใช้แล้ว
              }
            }}
          >
            {linkProfile.isPending ? "ກຳລັງເຊື່ອມ..." : "ເຊື່ອມ ແລະ ສົ່ງຂໍ້ຄວາມ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
